import { Image } from 'antd-mobile'

import xiajia from '@/assets/images/xiajia.svg'
import BookCard from '@/components/BookCard'
import GlobalCard from '@/components/GlobalCard'
import { useCustomNavigate, useVisibilityReport } from '@/hooks'
import { historyGetByResource, IThreadListResp } from '@/api'

import Icon from '../Icon'
import { cn, customJumpHref, handleDetailViewTrack, handleReadingButtonShowTrack } from '@/utils'
import { CONTENT_ROUTE_PATH, EPublishType } from '@/types'
import { isSuccess } from '@/axios'
import { useAtomValue } from 'jotai'
import { loginStateAtom, useGlobalLoading } from '@/store'

interface PropsIn {
  className?: string
  data: IThreadListResp
  afterOnClick?: () => void
  onReadBefore?: () => void
}

const DynamicItem = (props: PropsIn) => {
  const { className, data, afterOnClick, onReadBefore } = props
  const { customNavigator } = useCustomNavigate()
  const isLogin = useAtomValue(loginStateAtom)
  const { setGlobalLoading } = useGlobalLoading()

  const dataNotFound =
    data.type === 2 &&
    ((Array.isArray(data.resource_data) && data.resource_data.length === 0) ||
      Object.keys(data.resource_data).length === 0 ||
      !data.resource_data.resource_id)

  const handleRead = async () => {
    if (dataNotFound) {
      return customNavigator('/notFound')
    }

    if (isLogin) {
      try {
        setGlobalLoading(true)
        const resp = await historyGetByResource({
          resource_id: data.resource_id || '',
          resource_type: Number(data.resource_type),
        })

        if (isSuccess(resp) && resp.data.is_history) {
          return customJumpHref(
            `/detail/${CONTENT_ROUTE_PATH.get(
              Number(data.resource_type) as unknown as EPublishType,
            )}/${data.resource_type}-${data.resource_id}-${resp.data.history_data.chapter_id}`,
          )
        } else {
          customJumpHref(
            `/detail/${CONTENT_ROUTE_PATH.get(Number(data.resource_type) as unknown as EPublishType)}/${data.resource_type}-${data.resource_id}`,
          )
        }
      } catch (error) {
        console.error(error)
      } finally {
        setGlobalLoading(false)
      }
    } else {
      customJumpHref(
        `/detail/${CONTENT_ROUTE_PATH.get(Number(data.resource_type) as unknown as EPublishType)}/${data.resource_type}-${data.resource_id}`,
      )
    }
  }
  useVisibilityReport('dynamicItemReadBtn', () => {
    handleReadingButtonShowTrack({
      from: 7,
    })
  })

  return (
    <>
      {!data.created_at ? (
        <div
          className={cn(
            'mb-[22px] flex h-[284px] w-full cursor-pointer flex-col items-center justify-center rounded-[15px] bg-color-white-trans10 p-[30px]',
          )}
          onClick={() => {
            customNavigator('/notFound')
          }}
        >
          <img src={xiajia} alt='' />
          <span className='relative mx-[15px] flex items-center justify-center font-geologica text-[26px] font-normal leading-[34px] text-base-text-color opacity-70'>
            <span className='mr-[15px] inline-block h-[2px] w-[149px] rounded-none bg-gradient-to-r from-white to-transparent'></span>
            Content Removed
            <span className='ml-[15px] inline-block h-[2px] w-[149px] rounded-none bg-gradient-to-l from-white to-transparent'></span>
          </span>
        </div>
      ) : (
        <GlobalCard
          className={cn('mb-[22px] cursor-pointer', className)}
          onClick={() => {
            afterOnClick?.()
            const type = data.cmty_type === 1 ? 4 : 5
            handleDetailViewTrack({
              resource_type: type,
              resource_id: data?.cmty_id ?? '',
              resource_tag_id: '',
              cover_from: '',
              resource_genre: '',
            })
            customNavigator(`/detail/content/${data.cmty_type}/${data.cmty_id}`)
          }}
          userData={data}
          extra={
            <div className={cn('flex items-center')}>
              <Icon className={cn('mr-[9px] flex h-[22px] w-[22px] items-center justify-center')} name='HeartSmall'></Icon>
              <span className='font-geologica text-[19px] font-[400] text-color-white-trans40'>{!data.like_num ? 'Like' : data.like_num}</span>
            </div>
          }
        >
          <div>
            <div className={cn('mb-[19px] line-clamp-1 break-words font-geologica text-[30px] font-normal leading-[41px] text-base-text-color')}>
              {data.title}
            </div>
            <div className={cn('line-clamp-2 break-words font-geologica text-[26px] font-normal leading-[41px] text-color-white-trans70')}>
              {data.content}
            </div>
            {data.pic_urls?.length ? (
              <div className={cn('picBox', 'mt-[35px] grid gap-[19px]', 'grid-cols-3 md:grid-cols-6')}>
                {data.pic_urls?.map((item) => {
                  return (
                    <Image
                      key={item}
                      src={item}
                      className={cn('aspect-square overflow-hidden rounded-[15px]')}
                      // lazy
                      fit='cover'
                    />
                  )
                })}
              </div>
            ) : data.type === 2 ? (
              <div className={cn('relative mt-[35px] h-[271px] w-full rounded-[20px] bg-discover-card-content-bg-color p-[30px]')}>
                <BookCard
                  className={cn('h-full w-full')}
                  pictureClassName={cn('h-[210px] w-[149px] rounded-[8px]')}
                  infoClassName={cn(
                    'm-0 ml-[19px] h-full flex-1 [&>span:first-child]:mb-[17px] [&>span:first-child]:max-h-[75px] [&>span:first-child]:h-auto [&>span:first-child]:whitespace-normal [&>span:first-child]:text-[26px] [&>span:first-child]:font-normal [&>span:first-child]:leading-[37px]',
                  )}
                  data={data.resource_data}
                  options={
                    <button
                      className={cn(
                        'absolute bottom-[50px] right-[37px] flex h-[45px] w-[86px] items-center justify-center rounded-[7px] !border-0 bg-button-bg p-0 font-geologica text-[22px] font-normal text-button-text-color',
                      )}
                      id='dynamicItemReadBtn'
                      onClick={(e) => {
                        e.stopPropagation()
                        onReadBefore?.()
                        handleRead()
                      }}
                    >
                      Read
                    </button>
                  }
                />
              </div>
            ) : null}
          </div>
        </GlobalCard>
      )}
    </>
  )
}

export default DynamicItem
